import React, { useEffect, useState, useRef } from 'react';
import $ from 'jquery';
import { Icon } from '@iconify/react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import {
  getExpenseCodes,
  createExpenseCode,
  updateExpenseCode,
  deleteExpenseCode,
  checkExpenseCodeDuplicate,
} from '../api/expense-codeAPI';
import { fetchData } from '../utils/apiHelpers';

const ExpenseCodeListLayer = () => {
  const userProfile = useUserProfile();
  const [expenseCodeList, setExpenseCodeList] = useState([]);
  const [newCode, setNewCode] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});

  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const fetchCodes = () => {
    fetchData(getExpenseCodes, {}, setExpenseCodeList);
  };


  const validateForm = () => {
    const newErrors = {};
    if (!newCode.code?.trim()) newErrors.code = 'Code is required';
    if (!newCode.name?.trim()) newErrors.name = 'Name is required';
    if (!newCode.accCode?.trim()) newErrors.accCode = 'Account Code is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    fetchCodes();
  }, []);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
    if (expenseCodeList.length > 0) {
      dtRef.current = $(tableRef.current).DataTable({
        pageLength: 25,
        dom: 'rtp',
        responsive: false,
        language: { paginate: { first: '', last: '', next: '', previous: '' } },
      });
    }
    return () => {
      if (dtRef.current) {
        dtRef.current.destroy(true);
        dtRef.current = null;
      }
    };
  }, [expenseCodeList]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.search(searchTerm).draw();
    }
  }, [searchTerm]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewCode((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = (item) => {
    setNewCode(item);
    setEditingId(item.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this expense code?')) return;
    try {
      await deleteExpenseCode(id);
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
      await fetchCodes();
    } catch (error) {
      console.error('Error deleting expense code:', error);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      const dup = await checkExpenseCodeDuplicate({
        code: newCode.code?.trim(),
        name: newCode.name?.trim(),
        id: editingId,
      });
      const dupErrors = {};
      if (dup.code) dupErrors.code = 'Code already exists';
      if (dup.name) dupErrors.name = 'Name already exists';
      if (Object.keys(dupErrors).length) {
        setErrors(dupErrors);
        return;
      }
      const payload = {};
      for (const key in newCode) {
        if (newCode[key] !== null && typeof newCode[key] !== 'object') {
          payload[key] = newCode[key];
        }
      }

      if (editingId) {
        await updateExpenseCode(editingId, payload);
      } else {
        await createExpenseCode(payload);
      }

      setShowModal(false);
      setEditingId(null);

      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }

      setNewCode({});
      await fetchCodes();
    } catch (error) {
      console.error('Error saving expense code:', error);
    }
  };

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex justify-content-between align-items-center p-3">
        <input
          type="text"
          className="form-control w-auto"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {userProfile?.privileges?.includes('expense-code:create') && (
          <button
            className="btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
            onClick={() => {
              setNewCode({});
              setEditingId(null);
              setShowModal(true);
            }}
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl" />
            Add Expense Code
          </button>
        )}
      </div>

      <div className="card-body p-0">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table mb-0 w-100 rounded-0" ref={tableRef}>
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Account Code</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {expenseCodeList.map((item) => (
                <tr key={item.id}>
                  <td><strong>{item.code}</strong></td>
                  <td>{item.name}</td>
                  <td>{item.accCode}</td>
                  <td>
                    <div className="d-flex gap-2">
                      {userProfile?.privileges?.includes('expense-code:update') && (
                        <button
                          className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleEdit(item)}
                        >
                          <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                        </button>
                      )}
                      {userProfile?.privileges?.includes('expense-code:delete') && (
                        <button
                          className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleDelete(item.id)}
                        >
                          <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Expense Code' : 'New Expense Code'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Code *</label>
                    <input
                      className={`form-control ${errors.code ? 'is-invalid' : ''}`}
                      name="code"
                      value={newCode.code || ''}
                      onChange={handleInputChange}
                    />
                    {errors.code && <div className="invalid-feedback">{errors.code}</div>}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Name *</label>
                    <input
                      className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                      name="name"
                      value={newCode.name || ''}
                      onChange={handleInputChange}
                    />
                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Account Code *</label>
                    <input
                      className={`form-control ${errors.accCode ? 'is-invalid' : ''}`}
                      name="accCode"
                      value={newCode.accCode || ''}
                      onChange={handleInputChange}
                    />
                    {errors.accCode && <div className="invalid-feedback">{errors.accCode}</div>}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                <button className="btn btn-primary" onClick={handleSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseCodeListLayer;
